import {
  FacetSource,
  PublicFacetSource
} from "components/FindYourCruise/Components/Autocomplete/buildSources";
import { BrowserRouteState } from "components/GlobalSearch/routing";
import type { IndexUiState, UiState } from "instantsearch.js";
import { Language } from "src/utils/localization/languages/types";

export const uiStateToSerializableParams = (
  uiState: UiState,
  indexName: string
): BrowserRouteState => searchStateToSerializableParams(uiState[indexName]);

export const searchStateToSerializableParams = (
  searchState: UiState[string]
): BrowserRouteState => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { configure, ...filteredSearchState } = searchState;
  return {
    ...filteredSearchState,
    refinementList: toPublicFacet(filteredSearchState?.refinementList)
  };
};

const localizedFacets = [
  "destinationNames",
  "countryNames"
] as LocalizedFacet[];
type LocalizedFacet = "destinationNames" | "countryNames";

export const toLocalizedFacets = (
  language: Language,
  refinementList: IndexUiState["refinementList"]
): IndexUiState["refinementList"] => {
  console.debug("localizeFacet", refinementList);
  if (!refinementList) return refinementList;

  return Object.keys(refinementList).reduce(
    (acc, facetName) => {
      if (isLocalizableFacetSource(facetName)) {
        acc[localizedFacetSource(language, facetName)] =
          refinementList[facetName];
        delete acc[unlocalizedFacetSource(facetName)];
      }
      return acc;
    },
    { ...refinementList }
  );
};

const toPublicFacet = (refinementList: IndexUiState["refinementList"]) => {
  if (!refinementList) return refinementList;

  return Object.keys(refinementList).reduce(
    (acc, facetName) => {
      if (isLocalizedFacetSource(facetName)) {
        acc[unlocalizedFacetSource(facetName)] = refinementList[facetName];
        delete acc[facetName];
      }
      return acc;
    },
    { ...refinementList }
  );
};

export const unlocalizedFacetSource = <T extends string, C extends string>(
  facet: T
): C => facet.split(".", 2)[0] as C;

export const localizedFacetSource = <
  T extends PublicFacetSource,
  C extends FacetSource
>(
  language: Language,
  facet: T
): C => {
  if (!isLocalizedFacetSource(facet)) return facet as unknown as C;

  const sanityCode = language.sanityCode;
  return `${facet}.${sanityCode}` as C;
};

const isLocalizedFacetSource = (
  facetName: string
): facetName is FacetSource => {
  return localizedFacets.includes(unlocalizedFacetSource(facetName));
};

const isLocalizableFacetSource = (
  facetName: string
): facetName is LocalizedFacet => {
  return localizedFacets.includes(unlocalizedFacetSource(facetName));
};
